import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { GameAttemptResult, GameAttemptSubmission, LessonGamePlay, MatchAnswer, PlayItem } from 'src/app/models/module/lessongame.model';
import { ClassmanagementService } from 'src/app/services/classmanagement.service';
import { checkGameAttempt } from '../check-game-attempt';

interface MatchSlot {
  def: PlayItem;
  placed: PlayItem[];
  state: 'unattempted' | 'correct' | 'incorrect';
}

@Component({
  selector: 'app-match-game',
  templateUrl: './match-game.component.html',
  styleUrls: ['./match-game.component.css']
})
export class MatchGameComponent implements OnChanges {
  @Input() game: LessonGamePlay;
  @Output() attempted = new EventEmitter<GameAttemptResult>();

  unplaced: PlayItem[] = [];
  slots: MatchSlot[] = [];
  connectedListIds: string[] = [];
  checking = false;
  result: GameAttemptResult = null;

  constructor(private classmanager: ClassmanagementService) { }

  ngOnChanges(): void {
    this.reset();
  }

  reset(): void {
    this.result = null;
    this.unplaced = [...(this.game?.terms || [])];
    this.slots = (this.game?.definitions || []).map(def => ({ def, placed: [], state: 'unattempted' as const }));
    this.connectedListIds = ['unplaced', ...this.slots.map(s => 'slot-' + s.def.id)];
  }

  drop(event: CdkDragDrop<PlayItem[]>, target: PlayItem[]): void {
    if (this.result) { return; }
    if (target !== this.unplaced && target.length >= 1) {
      // the slot already holds a term — bump it back to the pool before accepting the new one
      this.unplaced.push(...target.splice(0));
    }
    if (event.previousContainer === event.container) {
      moveItemInArray(target, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, target, event.previousIndex, event.currentIndex);
    }
  }

  get allPlaced(): boolean {
    return this.unplaced.length === 0 && this.slots.every(s => s.placed.length === 1);
  }

  checkAnswer(): void {
    if (!this.allPlaced || this.checking) { return; }
    this.checking = true;
    const submission = new GameAttemptSubmission();
    submission.gameId = this.game.id;
    submission.matches = this.slots.map(s => new MatchAnswer(s.placed[0].id, s.def.id));

    checkGameAttempt(this.game, submission, this.classmanager).subscribe(
      (result: GameAttemptResult) => {
        this.result = result;
        this.slots.forEach(s => {
          s.state = result.correctTermIds.includes(s.placed[0].id) ? 'correct' : 'incorrect';
        });
        this.checking = false;
        this.attempted.emit(result);
      },
      () => { this.checking = false; }
    );
  }

  tryAgain(): void {
    this.reset();
  }
}
